"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Lexes a template string.
 *
 * @author Eric MORAND <eric.morand@gmail.com>
 */
const token_1 = require("./token");
const token_stream_1 = require("./token-stream");
const syntax_1 = require("./error/syntax");
let preg_quote = require('locutus/php/pcre/preg_quote');
let rtrim = require('locutus/php/strings/rtrim');
let merge = require('merge');
let safeCChars = ['b', 'f', 'n', 'r', 't', 'v', '0', '\'', '"', '\\'];
let stripcslashes = function (string) {
    return string.replace(/\\(.)/g, function (match, char) {
        if (safeCChars.includes(char)) {
            return new Function('return "' + match + '"')();
        }
        else {
            return char;
        }
    });
};
class TwingLexer {
    constructor(env, options = {}) {
        this.env = env;
        this.options = merge({
            interpolation: ['#{', '}'],
            tag_block: ['{%', '%}'],
            tag_comment: ['{#', '#}'],
            tag_variable: ['{{', '}}'],
            whitespace_trim: '-',
            whitespace_line_trim: '~',
            whitespace_line_chars: ' \\t\\0\\x0B',
        }, options);
        this.regexes = {
            interpolation_end: new RegExp('^\\s*' + this.options.interpolation[1]),
            interpolation_start: new RegExp('^' + this.options.interpolation[0] + '\\s*'),
            lex_block: new RegExp('^\\s*' +
                '(?:' +
                this.options.whitespace_trim + this.options.tag_block[1] + '\\s*\\n?' +
                '|' +
                this.options.whitespace_line_trim + this.options.tag_block[1] + '[' + this.options.whitespace_line_chars + ']*' +
                '|' +
                this.options.tag_block[1] + '\\n?' +
                ')'),
            lex_block_line: new RegExp('^\\s*line\\s+(\\d+)\\s*' + this.options.tag_block[1]),
            lex_block_raw: new RegExp('^\\s*' +
                'verbatim' +
                '\\s*' +
                '(?:' +
                this.options.whitespace_trim + this.options.tag_block[1] + '\\s*' +
                '|' +
                this.options.whitespace_line_trim + this.options.tag_block[1] + '[' + this.options.whitespace_line_chars + ']*' +
                '|' +
                this.options.tag_block[1] +
                ')'),
            lex_comment: new RegExp('(?:' +
                this.options.whitespace_trim + this.options.tag_comment[1] + '\\s*' +
                '|' +
                this.options.whitespace_line_trim + this.options.tag_comment[1] + '[' + this.options.whitespace_line_chars + ']*' +
                '|' +
                this.options.tag_comment[1] + '\\n?' +
                ')'),
            lex_raw_data: new RegExp(this.options.tag_block[0] +
                '(' +
                this.options.whitespace_trim +
                '|' +
                this.options.whitespace_line_trim +
                ')?\\s*' +
                '(?:endverbatim)' +
                '\\s*' +
                '(?:' +
                this.options.whitespace_trim + this.options.tag_block[1] + '\\s*' +
                '|' +
                this.options.whitespace_line_trim + this.options.tag_block[1] + '[' + this.options.whitespace_line_chars + ']*' +
                '|' +
                this.options.tag_block[1] +
                ')'),
            lex_tokens_start: new RegExp('(' +
                this.options.tag_variable[0] + '|' +
                this.options.tag_block[0] + '|' +
                this.options.tag_comment[0] +
                ')(' +
                this.options.whitespace_trim +
                '|' +
                this.options.whitespace_line_trim +
                ')?', 'g'),
            lex_var: new RegExp('^\\s*' +
                '(?:' +
                this.options.whitespace_trim + this.options.tag_variable[1] + '\\s*' +
                '|' +
                this.options.whitespace_line_trim + this.options.tag_variable[1] + '[' + this.options.whitespace_line_chars + ']*' +
                '|' +
                this.options.tag_variable[1] +
                ')'),
            operator: this.getOperatorRegEx()
        };
    }
    tokenize(source) {
        this.source = source;
        // in PHP str_replace returns an empty string if called on a non-string object
        if (typeof source.getCode() !== 'string') {
            this.code = '';
        }
        else {
            this.code = source.getCode().replace(/\r\n|\r/g, '\n');
        }
        this.cursor = 0;
        this.end = this.code.length;
        this.lineno = 1;
        this.columnno = 1;
        this.tokens = [];
        this.state = TwingLexer.STATE_DATA;
        this.states = [];
        this.brackets = [];
        this.position = -1;
        this.positions = [];
        // find all token starts in one go
        let match;
        while ((match = this.regexes.lex_tokens_start.exec(this.code)) !== null) {
            this.positions.push(match);
        }
        while (this.cursor < this.end) {
            // dispatch to the lexing functions depending on the current state
            // https://stackoverflow.com/questions/45197320/typescript-switch-statement-throws-not-comparable-to-type-error
            switch (+this.state) {
                case TwingLexer.STATE_DATA:
                    this.lexData();
                    break;
                case TwingLexer.STATE_BLOCK:
                    this.lexBlock();
                    break;
                case TwingLexer.STATE_VAR:
                    this.lexVar();
                    break;
                case TwingLexer.STATE_STRING:
                    this.lexString();
                    break;
                case TwingLexer.STATE_INTERPOLATION:
                    this.lexInterpolation();
                    break;
            }
        }
        this.pushToken(token_1.TwingToken.EOF_TYPE);
        if (this.brackets.length > 0) {
            let bracket = this.brackets.pop();
            let expect = bracket.value;
            let lineno = bracket.line;
            throw new syntax_1.TwingErrorSyntax(`Unclosed "${expect}".`, lineno, this.source);
        }
        return new token_stream_1.TwingTokenStream(this.tokens, this.source);
    }
    lexData() {
        // if no matches are left we return the rest of the template as simple text token
        if (this.position === (this.positions.length - 1)) {
            let text = this.code.substring(this.cursor);
            this.pushToken(token_1.TwingToken.TEXT_TYPE, text);
            this.moveCursor(text);
            this.moveCoordinates(text);
            return;
        }
        // Find the first token after the current cursor
        let position = this.positions[++this.position];
        while (position.index < this.cursor) {
            if (this.position == (this.positions.length - 1)) {
                return;
            }
            position = this.positions[++this.position];
        }
        // push the template text first
        let text;
        let textContent;
        text = textContent = this.code.substr(this.cursor, position.index - this.cursor);
        if (this.positions[this.position][2]) {
            if (this.positions[this.position][2] === this.options.whitespace_trim) {
                // whitespace_trim detected ({%-, {{- or {#-)
                text = rtrim(text);
            }
            else {
                // whitespace_line_trim detected ({%~, {{~ or {#~)
                text = rtrim(text, this.options.whitespace_line_chars);
            }
        }
        this.pushToken(token_1.TwingToken.TEXT_TYPE, text);
        this.moveCoordinates(textContent);
        this.moveCursor(textContent + position[0]);
        switch (position[1]) {
            case this.options.tag_comment[0]:
                this.pushToken(token_1.TwingToken.COMMENT_START_TYPE);
                this.moveCoordinates(position[0]);
                this.lexComment();
                break;
            case this.options.tag_block[0]:
                // raw data?
                let match;
                if ((match = this.regexes.lex_block_raw.exec(this.code.substring(this.cursor))) !== null) {
                    this.moveCursor(match[0]);
                    this.moveCoordinates(position[0] + match[0]);
                    this.lexRawData();
                }
                // {% line \d+ %}
                else if ((match = this.regexes.lex_block_line.exec(this.code.substring(this.cursor))) !== null) {
                    this.moveCursor(match[0]);
                    this.moveCoordinates(position[0] + match[0]);
                    this.lineno = parseInt(match[1]);
                }
                else {
                    this.pushToken(token_1.TwingToken.BLOCK_START_TYPE);
                    this.moveCoordinates(position[0]);
                    this.pushState(TwingLexer.STATE_BLOCK);
                    this.currentVarBlockLine = this.lineno;
                }
                break;
            case this.options.tag_variable[0]:
                this.pushToken(token_1.TwingToken.VAR_START_TYPE);
                this.moveCoordinates(position[0]);
                this.pushState(TwingLexer.STATE_VAR);
                this.currentVarBlockLine = this.lineno;
                break;
        }
    }
    lexBlock() {
        let string = this.code.substring(this.cursor);
        let match;
        if ((this.brackets.length < 1) && ((match = this.regexes.lex_block.exec(string)) !== null)) {
            this.pushToken(token_1.TwingToken.BLOCK_END_TYPE);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
            this.popState();
        }
        else {
            this.lexExpression();
        }
    }
    lexVar() {
        let match;
        if ((this.brackets.length < 1) && ((match = this.regexes.lex_var.exec(this.code.substring(this.cursor))) !== null)) {
            let text = this.handleLeadingLineFeeds(match[0]);
            this.pushToken(token_1.TwingToken.VAR_END_TYPE);
            this.moveCursor(text);
            this.moveCoordinates(text);
            this.popState();
        }
        else {
            this.lexExpression();
        }
    }
    lexExpression() {
        let match;
        let candidate = this.code.substring(this.cursor);
        let punctuationCandidate;
        // whitespace
        if ((match = /^\s+/.exec(candidate)) !== null) {
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
            if (this.cursor >= this.end) {
                throw new syntax_1.TwingErrorSyntax(`Unclosed "${this.state === TwingLexer.STATE_BLOCK ? 'block' : 'variable'}".`, this.currentVarBlockLine, this.source);
            }
        }
        candidate = this.code.substring(this.cursor);
        punctuationCandidate = candidate.substr(0, 1);
        // arrow function
        if (this.code[this.cursor] === '=' && this.code[this.cursor + 1] === '>') {
            this.pushToken(token_1.TwingToken.ARROW_TYPE, '=>');
            this.moveCursor('=>');
            this.moveCoordinates('=>');
        }
        // operators
        else if ((match = this.regexes.operator.exec(candidate)) !== null) {
            let tokenValue = match[0].replace(/\s+/, ' ');
            this.pushToken(token_1.TwingToken.OPERATOR_TYPE, tokenValue);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // names
        else if ((match = TwingLexer.REGEX_NAME.exec(candidate)) !== null) {
            this.pushToken(token_1.TwingToken.NAME_TYPE, match[0]);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // numbers
        else if ((match = TwingLexer.REGEX_NUMBER.exec(candidate)) !== null) {
            this.pushToken(token_1.TwingToken.NUMBER_TYPE, Number(match[0]));
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // punctuation
        else if (TwingLexer.PUNCTUATION.indexOf(punctuationCandidate) > -1) {
            // opening bracket
            if ('([{'.indexOf(punctuationCandidate) > -1) {
                this.brackets.push({
                    value: punctuationCandidate,
                    line: this.lineno
                });
            }
            // closing bracket
            else if (')]}'.indexOf(punctuationCandidate) > -1) {
                if (this.brackets.length < 1) {
                    throw new syntax_1.TwingErrorSyntax(`Unexpected "${punctuationCandidate}".`, this.lineno, this.source);
                }
                let bracket = this.brackets.pop();
                let lineno = bracket.line;
                let expect = bracket.value
                    .replace('(', ')')
                    .replace('[', ']')
                    .replace('{', '}');
                if (punctuationCandidate != expect) {
                    throw new syntax_1.TwingErrorSyntax(`Unclosed "${expect}".`, lineno, this.source);
                }
            }
            this.pushToken(token_1.TwingToken.PUNCTUATION_TYPE, punctuationCandidate);
            this.cursor++;
            this.columnno++;
        }
        // strings
        else if ((match = TwingLexer.REGEX_STRING.exec(candidate)) !== null) {
            this.pushToken(token_1.TwingToken.STRING_TYPE, stripcslashes(match[0].slice(1, -1)));
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // opening double quoted string
        else if ((match = TwingLexer.REGEX_DQ_STRING_DELIM.exec(candidate)) !== null) {
            this.brackets.push({
                value: '"',
                line: this.lineno
            });
            this.pushState(TwingLexer.STATE_STRING);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // unlexable
        else {
            throw new syntax_1.TwingErrorSyntax(`Unexpected character "${candidate}" in "${this.code}".`, this.lineno, this.source);
        }
    }
    lexRawData() {
        let match = this.regexes.lex_raw_data.exec(this.code.substring(this.cursor));
        if (!match) {
            throw new syntax_1.TwingErrorSyntax('Unexpected end of file: Unclosed "verbatim" block.', this.lineno, this.source);
        }
        let text = this.code.substr(this.cursor, match.index);
        this.moveCursor(text + match[0]);
        if (match[1]) {
            if (match[1] === this.options.whitespace_trim) {
                // whitespace_trim detected ({%-, {{- or {#-)
                text = rtrim(text);
            }
            else {
                // whitespace_line_trim detected ({%~, {{~ or {#~)
                // don't trim \r and \n
                text = rtrim(text, " \t\0\x0B");
            }
        }
        this.pushToken(token_1.TwingToken.TEXT_TYPE, text);
        this.moveCoordinates(text + match[0]);
    }
    lexComment() {
        let match = this.regexes.lex_comment.exec(this.code.substring(this.cursor));
        if (!match) {
            throw new syntax_1.TwingErrorSyntax('Unclosed comment.', this.lineno, this.source);
        }
        let text = this.code.substr(this.cursor, match.index);
        this.pushToken(token_1.TwingToken.TEXT_TYPE, text);
        this.moveCursor(text);
        this.moveCoordinates(text);
        this.pushToken(token_1.TwingToken.COMMENT_END_TYPE);
        this.moveCursor(match[0]);
        this.moveCoordinates(match[0]);
    }
    lexString() {
        let match;
        if ((match = this.regexes.interpolation_start.exec(this.code.substring(this.cursor))) !== null) {
            this.brackets.push({
                value: this.options.interpolation[0],
                line: this.lineno
            });
            this.pushToken(token_1.TwingToken.INTERPOLATION_START_TYPE);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
            this.pushState(TwingLexer.STATE_INTERPOLATION);
        }
        else if (((match = TwingLexer.REGEX_DQ_STRING_PART.exec(this.code.substring(this.cursor))) !== null) && (match[0].length > 0)) {
            this.pushToken(token_1.TwingToken.STRING_TYPE, stripcslashes(match[0]));
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
        }
        // unit: else path not coverable
        // else if ((TwingLexer.REGEX_DQ_STRING_DELIM.exec(this.code.substring(this.cursor))) !== null) {
        else {
            let { value: expect, line: lineno } = this.brackets.pop();
            // unit: if path not coverable
            // if (this.code[this.cursor] != '"') {
            //     throw new TwingErrorSyntax(`Unclsosed "%${expect}".`, lineno, this.source);
            // }
            this.popState();
            this.cursor++;
            this.columnno++;
        }
    }
    lexInterpolation() {
        let match;
        let bracket = this.brackets[this.brackets.length - 1];
        if (this.options.interpolation[0] === bracket.value && (match = this.regexes.interpolation_end.exec(this.code.substring(this.cursor))) !== null) {
            this.brackets.pop();
            this.pushToken(token_1.TwingToken.INTERPOLATION_END_TYPE);
            this.moveCursor(match[0]);
            this.moveCoordinates(match[0]);
            this.popState();
        }
        else {
            this.lexExpression();
        }
    }
    handleLeadingLineFeeds(text) {
        let match = /^\n+/.exec(text);
        if (match) {
            let lineno = this.lineno;
            this.moveCoordinates(match[0]);
            let lineCount = this.lineno - lineno;
            this.cursor += lineCount;
            text = text.substring(lineCount);
        }
        return text;
    }
    moveCursor(text) {
        this.cursor += text.length;
    }
    moveCoordinates(text) {
        this.columnno += text.length;
        let lines = text.split('\n');
        let lineCount = lines.length - 1;
        if (lineCount > 0) {
            this.lineno += lineCount;
            this.columnno = 1 + lines[lineCount].length;
        }
    }
    getOperatorRegEx() {
        let operators = Array.from([
            ...['='],
            ...this.env.getUnaryOperators().keys(),
            ...this.env.getBinaryOperators().keys()
        ]);
        operators.sort(function (a, b) {
            return a.length > b.length ? -1 : 1;
        });
        let patterns = [];
        operators.forEach(function (operator) {
            let length = operator.length;
            let pattern;
            // an operator that ends with a character must be followed by
            // a whitespace or a parenthesis
            if (new RegExp('[A-Za-z]').test(operator[length - 1])) {
                pattern = preg_quote(operator, '/') + '(?=[\\s()])';
            }
            else {
                pattern = preg_quote(operator, '/');
            }
            // an operator with a space can be any amount of whitespaces
            pattern = pattern.replace(/\s+/, '\\s+');
            patterns.push('^' + pattern);
        });
        return new RegExp(patterns.join('|'));
    }
    ;
    pushToken(type, value = null) {
        if ((token_1.TwingToken.TEXT_TYPE === type) && (value.length < 1)) {
            return;
        }
        this.tokens.push(new token_1.TwingToken(type, value, this.lineno, this.columnno));
    }
    pushState(state) {
        this.states.push(this.state);
        this.state = state;
    }
    /**
     * @return TwingLexerState
     */
    popState() {
        // unit: if path not coverable
        // if (this.states.length === 0) {
        //     throw new Error('Cannot pop state without a previous state.');
        // }
        this.state = this.states.pop();
    }
}
exports.TwingLexer = TwingLexer;
TwingLexer.STATE_DATA = 0;
TwingLexer.STATE_BLOCK = 1;
TwingLexer.STATE_VAR = 2;
TwingLexer.STATE_STRING = 3;
TwingLexer.STATE_INTERPOLATION = 4;
TwingLexer.REGEX_NAME = /^[a-zA-Z_\x7f-\xff][a-zA-Z0-9_\x7f-\xff]*/;
TwingLexer.REGEX_NUMBER = /^[0-9]+(?:\.[0-9]+)?/;
TwingLexer.REGEX_STRING = /^"([^#"\\]*(?:\\.[^#"\\]*)*)"|^'([^'\\]*(?:\\.[^'\\]*)*)'/;
TwingLexer.REGEX_DQ_STRING_DELIM = /^"/;
TwingLexer.REGEX_DQ_STRING_PART = /^[^#"\\]*(?:(?:\\\\.|#(?!{))[^#"\\]*)*/;
TwingLexer.PUNCTUATION = '()[]{}?:.,|';
